import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OtpInput } from "@/components/auth/OtpInput";
import { authService } from "@/services";

type Step = "password" | "verify" | "success";

interface PasswordChangeFormProps {
  onSuccess: () => void;
  hasMfaEnabled: boolean;
  email: string;
}

export function PasswordChangeForm({
  onSuccess,
  hasMfaEnabled,
  email,
}: PasswordChangeFormProps) {
  const [step, setStep] = useState<Step>("password");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordsMatch = newPassword === confirmPassword;
  const isPasswordValid = newPassword.length >= 12;
  const canProceed =
    currentPassword && newPassword && confirmPassword && passwordsMatch && isPasswordValid;

  const handleSubmitPasswords = async () => {
    if (!canProceed) return;

    setIsLoading(true);
    setError(null);

    try {
      if (!hasMfaEnabled) {
        await authService.sendPasswordChangeOtp({ email });
      }
      setStep("verify");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initiate password change"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndChange = async () => {
    if (otpCode.length !== 6) return;

    setIsLoading(true);
    setError(null);

    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
        otpCode,
        verificationMethod: hasMfaEnabled ? "totp" : "email",
      });
      setStep("success");
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (hasMfaEnabled) return;

    setIsLoading(true);
    setError(null);

    try {
      await authService.sendPasswordChangeOtp({ email });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to resend code"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-4"
      >
        <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-500" />
        </div>
        <p className="font-medium text-sm">Password Changed Successfully</p>
        <p className="text-xs text-muted-foreground mt-1">
          Your password has been updated
        </p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      {step === "password" && (
        <motion.div
          key="password"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium">Current Password</label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">New Password</label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {newPassword && !isPasswordValid && (
              <p className="text-xs text-destructive">
                Password must be at least 12 characters
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Confirm New Password</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={handleSubmitPasswords}
            disabled={!canProceed || isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </motion.div>
      )}

      {step === "verify" && (
        <motion.div
          key="verify"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          className="space-y-4"
        >
          <div className="text-center mb-4">
            <p className="font-medium text-sm">
              {hasMfaEnabled
                ? "Enter Authenticator Code"
                : "Enter Verification Code"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {hasMfaEnabled
                ? "Enter the 6-digit code from your authenticator app"
                : `Enter the 6-digit code sent to ${email}`}
            </p>
          </div>

          <OtpInput
            value={otpCode}
            onChange={setOtpCode}
            disabled={isLoading}
            error={!!error}
            autoFocus
          />

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setStep("password");
                setOtpCode("");
                setError(null);
              }}
              disabled={isLoading}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleVerifyAndChange}
              disabled={otpCode.length !== 6 || isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Change Password"
              )}
            </Button>
          </div>

          {!hasMfaEnabled && (
            <button
              onClick={handleResendOtp}
              disabled={isLoading}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Didn't receive the code? Click to resend
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
